import store from '../store/store';
import tokenManager from './token-manager';
import { supabase } from './init-db';

const ownedTables = new Set([
  'onboarding_answers',
  'projects',
  'boards',
  'blocks',
  'tracking_elements',
  'status_pages',
  'feedback',
]);

function ensureClient() {
  if (!supabase) {
    throw new Error('Supabase is not configured');
  }

  return supabase;
}

function getCurrentUser() {
  return store.getters['auth/currentUser'];
}

function applyFilters(query, filters = {}) {
  return Object.entries(filters).reduce((nextQuery, [key, value]) => {
    if (Array.isArray(value)) {
      return nextQuery.in(key, value);
    }

    if (value === null) {
      return nextQuery.is(key, null);
    }

    return nextQuery.eq(key, value);
  }, query);
}

function withOwner(table, data) {
  if (!data || !ownedTables.has(table)) {
    return data;
  }

  const currentUser = getCurrentUser();
  if (!currentUser?.id) {
    throw new Error('User not authenticated');
  }

  if (Array.isArray(data)) {
    return data.map(row => (row.user_id ? row : { user_id: currentUser.id, ...row }));
  }

  if (data.user_id) {
    return data;
  }

  return {
    user_id: currentUser.id,
    ...data,
  };
}

async function ensureAuthReady() {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    throw new Error('User not authenticated');
  }

  const tokenValid = await tokenManager.ensureValidToken();
  if (!tokenValid) {
    throw new Error('Unable to obtain valid authentication token');
  }

  return currentUser;
}

export async function supabaseRequest(table, operation, data = null, filters = null) {
  await ensureAuthReady();
  const client = ensureClient();

  try {
    if (operation === 'select') {
      let query = client.from(table).select(data || '*');
      query = applyFilters(query, filters || {});
      const { data: rows, error } = await query;
      if (error) {
        throw error;
      }
      return rows || [];
    }

    if (operation === 'insert') {
      const payload = withOwner(table, data);
      const { data: rows, error } = await client.from(table).insert(payload).select();
      if (error) {
        throw error;
      }
      return rows || [];
    }

    if (operation === 'update') {
      if (!filters || Object.keys(filters).length === 0) {
        throw new Error('Filters are required for update operations');
      }

      let query = client.from(table).update(data || {});
      query = applyFilters(query, filters);
      const { data: rows, error } = await query.select();
      if (error) {
        throw error;
      }
      return rows || [];
    }

    if (operation === 'delete') {
      if (!filters || Object.keys(filters).length === 0) {
        throw new Error('Filters are required for delete operations');
      }

      let query = client.from(table).delete();
      query = applyFilters(query, filters);
      const { data: rows, error } = await query.select();
      if (error) {
        throw error;
      }
      return rows || [];
    }

    throw new Error(`Invalid operation: ${operation}`);
  } catch (error) {
    console.error('Supabase request error:', error);
    throw error;
  }
}

export async function selectFromSupabase(table, select = '*', filters = {}) {
  return await supabaseRequest(table, 'select', select, filters);
}

export async function fetchUserProfile() {
  const currentUser = await ensureAuthReady();
  const client = ensureClient();

  const { data, error } = await client
    .from('users')
    .select('*')
    .eq('id', currentUser.id)
    .maybeSingle();

  if (error) {
    console.error('Failed to fetch user profile:', error);
    return null;
  }

  return data;
}

export async function insertIntoSupabase(table, data) {
  const result = await supabaseRequest(table, 'insert', data);
  return result[0];
}

export async function updateInSupabase(table, data, filters) {
  return await supabaseRequest(table, 'update', data, filters);
}

export async function deleteFromSupabase(table, filters) {
  return await supabaseRequest(table, 'delete', null, filters);
}

export async function trackingRequest(operation, data = null, from = null, to = null) {
  await ensureAuthReady();
  const client = ensureClient();

  if (operation !== 'selectRange') {
    throw new Error('Only selectRange is supported for tracking requests');
  }

  let query = client
    .from('tracking_elements')
    .select(data || '*')
    .order('start_ts', { ascending: true });

  if (from && to) {
    query = query.lte('start_ts', to).or(`end_ts.is.null,end_ts.gte.${from}`);
  } else if (from) {
    query = query.or(`end_ts.is.null,end_ts.gte.${from}`);
  } else if (to) {
    query = query.lte('start_ts', to);
  }

  const { data: rows, error } = await query;
  if (error) {
    console.error('Tracking request error:', error);
    throw error;
  }

  return rows || [];
}

export async function selectTrackingRange(from, to, select = '*') {
  return await trackingRequest('selectRange', select, from, to);
}